import { isInThsApp } from './isInThsApp';
import { isInFuturesApp } from './isInFuturesApp';
import { isInIFindApp } from './isInIFindApp';
import { isInAInvestApp } from './isInAInvestApp';
import { isInWeixin } from './isInWeixin';
import { isInWeibo } from './isInWeibo';
import { isThsPcTyClient } from './isThsPcTyClient';
import { isThsPcYhClient } from './isThsPcYhClient';
import { isFuturesPcClient } from './isFuturesPcClient';

/**
 * 应用类型枚举
 */
export type AppType =
  | 'ths' // 同花顺移动端
  | 'thsPcTy' // 同花顺PC统一客户端
  | 'thsPcYh' // 同花顺PC远航版客户端
  | 'futures' // 期货通移动端
  | 'futuresPc' // 期货通PC客户端
  | 'ifind' // iFund客户端
  | 'ainvest' // A股投资客户端
  | 'weixin' // 微信环境
  | 'weibo' // 微博环境
  | 'unknown'; // 未知环境

/**
 * 获取当前应用类型
 *
 * 综合判断当前运行环境，返回对应的应用类型。
 * 检测优先级：PC客户端 > 移动端客户端 > 微信/微博环境
 *
 * @returns {AppType} 当前应用类型
 *
 * @example
 * ```ts
 * import { getApp } from '@fu/matrix';
 *
 * const appType = getApp();
 * console.log(`当前应用: ${appType}`);
 *
 * // 根据应用类型执行不同逻辑
 * switch (appType) {
 *   case 'thsPcTy':
 *     console.log('同花顺PC统一客户端');
 *     break;
 *   case 'thsPcYh':
 *     console.log('同花顺PC远航版客户端');
 *     break;
 *   case 'futuresPc':
 *     console.log('期货通PC客户端');
 *     break;
 *   case 'ths':
 *     console.log('同花顺移动端');
 *     break;
 *   case 'futures':
 *     console.log('期货通移动端');
 *     break;
 *   case 'weixin':
 *     console.log('微信环境');
 *     break;
 *   case 'weibo':
 *     console.log('微博环境');
 *     break;
 *   default:
 *     console.log('未知环境');
 * }
 * ```
 *
 * @since 1.0.0
 */
export function getApp(): AppType {
  try {
    // 1. 检测PC客户端（优先级最高）
    if (isThsPcTyClient()) {
      return 'thsPcTy';
    }

    if (isThsPcYhClient()) {
      return 'thsPcYh';
    }

    if (isFuturesPcClient()) {
      return 'futuresPc';
    }

    // 2. 检测移动端客户端
    if (isInThsApp()) {
      return 'ths';
    }

    if (isInFuturesApp()) {
      return 'futures';
    }

    if (isInIFindApp()) {
      return 'ifind';
    }

    if (isInAInvestApp()) {
      return 'ainvest';
    }

    // 3. 检测微信/微博环境
    if (isInWeixin()) {
      return 'weixin';
    }

    if (isInWeibo()) {
      return 'weibo';
    }

    // 4. 未知环境（服务端等）
    return 'unknown';
  } catch (error) {
    console.warn('检测应用类型失败:', error);
    return 'unknown';
  }
}